import React, { useContext, useState } from "react";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  ButtonGroup,
  Card,
  Flex,
  HStack,
  Heading,
  IconButton,
  Input,
  InputGroup,
  InputLeftAddon,
  Link,
  Spinner,
  Stack,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { IoIosRocket } from "react-icons/io";
import { LuWallet } from "react-icons/lu";
import { FaPlusSquare } from "react-icons/fa";
import { FiHelpCircle } from "react-icons/fi";
import { toast } from "react-toastify";
import Loader from "../../../utils/Loader/Loader";
import { AgencyContext } from "../AgencyProvider/AgencyProvider";
import ExtrasSection from "./ExtrasSection";
import PlansSection from "./PlansSection";
import useBillingPlans from "./BilingHook";
import useWallet from "./useWallet";
import useLookupWallet from "./useLookupWallet";
import { fetchController } from "../../../utils/FetchController/fetchController";
import { getHostDomain } from "../../../utils/utils";

const QUICK_AMOUNTS = [10, 25, 50, 100];
const MIN_AMOUNT = 5;
const HELP_URL = "https://docs.insighto.ai/getting-started/pricing";

const CURRENCY = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

// Prominent single-line wallet bar pinned above the accordions. Surfaces
// balance, recharge controls, and card management in one place so users
// never have to hunt through sections to add credits or change cards.
function WalletBar({ userDetails, isSubAccount }) {
  const { wallet, status, onRechargeSubmit } = useWallet();
  const { walletLookups } = useLookupWallet({ auto: true });
  const lookupKey = walletLookups[0]?.plan_lookup_key;

  const [amount, setAmount] = useState("");
  const [amountError, setAmountError] = useState("");
  const [pmLoading, setPmLoading] = useState(false);

  const baseUrl = getHostDomain();
  const balance = wallet?.wallet_balance;
  const balanceColor =
    typeof balance === "number"
      ? balance > 0
        ? "green.600"
        : "red.500"
      : "gray.400";
  const hasPM = Boolean(userDetails?.stripe_has_payment_method);
  const numericAmount = Number(amount);
  const validAmount = amount !== "" && numericAmount >= MIN_AMOUNT;
  const canPay = validAmount && hasPM && lookupKey;

  const handlePay = (e) => {
    e?.preventDefault();
    if (!validAmount) {
      setAmountError(`Min ${CURRENCY.format(MIN_AMOUNT)}`);
      return;
    }
    if (!hasPM || !lookupKey) return;
    onRechargeSubmit({ amount, lookup_key: lookupKey });
  };

  const handleManagePM = async () => {
    setPmLoading(true);
    try {
      const res = await fetchController(
        baseUrl + "/api/v1/billing/add-payment-method",
        "GET"
      );
      if (res?.status === "redirect" && res?.checkout_url) {
        // Same-tab redirect so the post-Stripe /billing/success page can
        // navigate the user straight back to a fresh /settings/billing
        // showing the updated card details.
        window.location.href = res.checkout_url;
        return;
      }
      toast.error("Unable to open payment method setup.");
    } catch {
      toast.error("Something went wrong");
    } finally {
      setPmLoading(false);
    }
  };

  return (
    <Box
      bg="white"
      borderWidth="1px"
      borderColor="blue.200"
      borderLeftWidth="4px"
      borderLeftColor="blue.500"
      borderRadius="md"
      boxShadow="sm"
      px={4}
      py={3}
      mb={3}
    >
      <Flex
        align="center"
        gap={4}
        wrap={{ base: "wrap", lg: "nowrap" }}
        rowGap={3}
      >
        {/* ─── Balance block ─── */}
        <HStack spacing={3} flexShrink={0} minW="180px">
          <Box
            bg="blue.50"
            color="blue.600"
            borderRadius="md"
            p={2}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <LuWallet size={20} />
          </Box>
          <Box>
            <HStack spacing={1} align="center">
              <Text
                fontSize="xs"
                textTransform="uppercase"
                color="gray.500"
                fontWeight="bold"
                letterSpacing="0.5px"
              >
                Wallet balance
              </Text>
              {!isSubAccount && (
                <Tooltip
                  label="How does the wallet work?"
                  placement="top"
                  hasArrow
                >
                  <Link
                    href={HELP_URL}
                    isExternal
                    color="gray.500"
                    _hover={{ color: "blue.500" }}
                    display="inline-flex"
                  >
                    <FiHelpCircle size={13} />
                  </Link>
                </Tooltip>
              )}
            </HStack>
            <Heading
              fontSize="2xl"
              lineHeight="1.1"
              color={balanceColor}
              mt={0.5}
            >
              {status === "loading" ? (
                <Spinner size="sm" />
              ) : typeof balance === "number" ? (
                CURRENCY.format(balance)
              ) : (
                "—"
              )}
            </Heading>
          </Box>
        </HStack>

        {/* ─── Recharge controls (inline) ─── */}
        <Stack
          as="form"
          onSubmit={handlePay}
          direction={{ base: "column", md: "row" }}
          align={{ base: "stretch", md: "center" }}
          spacing={2}
          flex={1}
          minW={0}
        >
          <ButtonGroup size="sm" isAttached variant="outline">
            {QUICK_AMOUNTS.map((amt) => {
              const selected = numericAmount === amt;
              return (
                <Button
                  key={amt}
                  onClick={() => {
                    setAmount(String(amt));
                    setAmountError("");
                  }}
                  colorScheme={selected ? "blue" : "gray"}
                  variant={selected ? "solid" : "outline"}
                >
                  ${amt}
                </Button>
              );
            })}
          </ButtonGroup>

          <InputGroup size="sm" maxW={{ base: "100%", md: "150px" }}>
            <InputLeftAddon>$</InputLeftAddon>
            <Input
              type="number"
              placeholder="Amount"
              value={amount}
              onChange={(e) => {
                setAmount(e.target.value);
                setAmountError("");
              }}
              isInvalid={Boolean(amountError)}
            />
          </InputGroup>

          <Tooltip
            isDisabled={hasPM}
            label="Add a payment method to recharge your wallet"
            placement="top"
            hasArrow
          >
            <Button
              type="submit"
              size="sm"
              colorScheme="blue"
              isLoading={status === "recharging"}
              isDisabled={!canPay}
              minW="90px"
            >
              Pay now
            </Button>
          </Tooltip>
        </Stack>

        {/* ─── Card management (always visible) ─── */}
        <HStack spacing={2} flexShrink={0} align="center">
          {hasPM && userDetails?.stripe_card_last4 && (
            <Text fontSize="sm" color="gray.700" whiteSpace="nowrap">
              {userDetails.stripe_card_brand
                ? userDetails.stripe_card_brand.charAt(0).toUpperCase() +
                  userDetails.stripe_card_brand.slice(1)
                : "Card"}{" "}
              •••• {userDetails.stripe_card_last4}
            </Text>
          )}
          <Tooltip
            label={
              hasPM
                ? "Update or replace the card on file"
                : "Add a card to enable wallet recharges"
            }
            placement="top"
            hasArrow
          >
            <Button
              size="sm"
              variant={hasPM ? "outline" : "solid"}
              colorScheme="blue"
              onClick={handleManagePM}
              isLoading={pmLoading}
            >
              {hasPM ? "Update card" : "Add card"}
            </Button>
          </Tooltip>
        </HStack>
      </Flex>

      {amountError && (
        <Text fontSize="xs" color="red.500" mt={1} ml={{ base: 0, lg: "200px" }}>
          {amountError}
        </Text>
      )}
    </Box>
  );
}

const Billing = () => {
  const agency = useContext(AgencyContext);
  const { myDetails, textColor } = agency;

  const isSalesBtnisvisible = agency.loading
    ? false
    : myDetails?.agency_id
    ? myDetails?.wallet === true
    : true;

  const {
    billingPlans,
    userDetails,
    isLoading,
    coupanBillingPlans,
    setIsLoading,
  } = useBillingPlans();

  // Re-ordered Plans → Extras. Wallet recharge has moved to the prominent
  // top bar (WalletBar); the old "Your Wallet" accordion is gone because
  // every wallet action is now reachable from the bar.
  //
  // coupanBillingPlans (the user's active plan when it isn't in the
  // standard list — e.g. hidden / coupon / lifetime) is passed to the
  // Plans section, where it gets clubbed with the active plans so the
  // user sees their current plan in the comparison row.
  const sections = [
    {
      id: "plans",
      title: (
        <Flex alignItems="center" gap={2}>
          <IoIosRocket />
          {isSalesBtnisvisible ? "Boosters" : "Plans"}
        </Flex>
      ),
      defaultOpen: true,
      component: (
        <PlansSection
          billingPlans={billingPlans}
          extraActivePlans={coupanBillingPlans}
          isSalesBtnisvisible={isSalesBtnisvisible}
          userDetails={userDetails}
        />
      ),
    },
    {
      id: "extras",
      title: (
        <Flex alignItems="center" gap={2}>
          <FaPlusSquare />
          Extras
        </Flex>
      ),
      defaultOpen: false,
      component: (
        <ExtrasSection
          userDetails={userDetails}
          setIsLoading={setIsLoading}
          isLoading={isLoading}
        />
      ),
    },
  ];

  const defaultIndex = sections
    .map((s, i) => (s.defaultOpen ? i : null))
    .filter((i) => i !== null);

  return (
    <Flex direction={"column"} gap={3} w={"100%"} h={"100%"}>
      <Flex gap={2} w={"100%"} alignItems="center" p={2}>
        <Heading color={textColor} fontSize="xl">
          {myDetails?.agency_id ? "Billing" : "Billing & Wallet"}
        </Heading>
      </Flex>

      {isLoading && <Loader />}

      <Box overflowY="auto" flex={1}>
        {isSalesBtnisvisible && (
          <WalletBar
            userDetails={userDetails}
            isSubAccount={Boolean(myDetails?.agency_id)}
          />
        )}
        <Accordion defaultIndex={defaultIndex} allowMultiple gap={2}>
          {sections.map(({ id, title, component }) => (
            <Card my={2} key={id} id={`billing-section-${id}`}>
              <AccordionItem border={"none"} borderBottom={"0 !important"}>
                <h2>
                  <AccordionButton>
                    <Box as="span" flex="1" textAlign="left" fontWeight="bold">
                      {title}
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                </h2>
                <AccordionPanel pb={4}>{component}</AccordionPanel>
              </AccordionItem>
            </Card>
          ))}
        </Accordion>
      </Box>
    </Flex>
  );
};

export default Billing;
